import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { loadTeamRatings } from "../utils/dataLoader";
import { TeamRating } from "../types";

type RankedTeam = TeamRating & { position: number };

const TeamRankings: React.FC = () => {
  const [teamRatings, setTeamRatings] = useState<TeamRating[]>([]);
  const [filteredTeams, setFilteredTeams] = useState<RankedTeam[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"rating" | "games" | "winRate">(
    "rating"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const teamsPerPage = 20;

  useEffect(() => {
    const loadData = async () => {
      try {
        const teams = await loadTeamRatings();
        // Filter out any teams with invalid data
        const validTeams = teams.filter(
          (team) =>
            team &&
            team.Team &&
            typeof team.Team === "string" &&
            team.Team.trim() !== ""
        );
        setTeamRatings(validTeams);
      } catch (error) {
        console.error("Error loading team ratings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const sorted = [...teamRatings].sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortBy) {
        case "rating":
          aValue = a.Rating;
          bValue = b.Rating;
          break;
        case "games":
          aValue = a["Games Played"];
          bValue = b["Games Played"];
          break;
        case "winRate":
          aValue =
            a["Games Played"] > 0
              ? (a["Games Won"] / a["Games Played"]) * 100
              : 0;
          bValue =
            b["Games Played"] > 0
              ? (b["Games Won"] / b["Games Played"]) * 100
              : 0;
          break;
        default:
          aValue = a.Rating;
          bValue = b.Rating;
      }

      if (sortOrder === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    // Position comes from the full list so searching never renumbers rows.
    const ranked = sorted.map((team, index) => ({ ...team, position: index + 1 }));
    const normalizedSearch = searchTerm.toLowerCase();
    const filtered = ranked.filter((team) =>
      team.Team?.toLowerCase().includes(normalizedSearch)
    );

    setFilteredTeams(filtered);
    setCurrentPage(1);
  }, [teamRatings, searchTerm, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredTeams.length / teamsPerPage);
  const startIndex = (currentPage - 1) * teamsPerPage;
  const endIndex = startIndex + teamsPerPage;
  const currentTeams = filteredTeams.slice(startIndex, endIndex);

  const getRatingBadgeClass = (rating: number): string => {
    if (rating < 1600) return "low";
    if (rating < 1800) return "medium";
    if (rating < 2000) return "high";
    return "very-high";
  };

  if (loading) {
    return (
      <div className="card">
        <h2>Wczytywanie…</h2>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2>Ranking par</h2>
        <p>Liczba par: {filteredTeams.length}</p>
      </div>

      <div className="search-container">
        <input
          type="text"
          name="pair-filter"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          placeholder="Szukaj par…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="card">
        <div className="sort-controls">
          <label className="sort-controls__field">
            <span className="sort-controls__label">Sortuj według</span>
            <select
              value={sortBy}
              onChange={(e) =>
                setSortBy(e.target.value as "rating" | "games" | "winRate")
              }
              className="sort-controls__select"
            >
              <option value="rating">Rating</option>
              <option value="games">Rozegrane mecze</option>
              <option value="winRate">Skuteczność</option>
            </select>
          </label>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="btn-primary"
          >
            {sortOrder === "asc" ? "↑ Rosnąco" : "↓ Malejąco"}
          </button>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Miejsce</th>
                <th>Nazwa pary</th>
                <th>Rating</th>
                <th>Rozegrane mecze</th>
                <th>Wygrane</th>
                <th>Przegrane</th>
                <th>Remisy</th>
                <th title="Procent wygranych spotkań. Najedź na wartość żeby zobaczyć remisy/przegrane.">% Wygranych</th>
                <th title="Sumaryczna zmiana ratingu od początku sezonu">Δ Rating</th>
                <th>Turnieje</th>
              </tr>
            </thead>
            <tbody>
              {currentTeams.map((team, index) => (
                <tr key={team.Team || `team-${startIndex + index}`}>
                  <td>{team.position}</td>
                  <td>
                    <Link
                      to={`/team/${encodeURIComponent(team.TeamKey ?? team.Team)}`}
                      className="player-link"
                    >
                      <strong>{team.Team || "Nieznana para"}</strong>
                    </Link>
                  </td>
                  <td>
                    <span
                      className={`rating-badge ${getRatingBadgeClass(
                        team.Rating
                      )}`}
                    >
                      {team.Rating}
                    </span>
                  </td>
                  <td>{team["Games Played"]}</td>
                  <td>{team["Games Won"]}</td>
                  <td>{team["Games Lost"]}</td>
                  <td>{team["Games Drawn"]}</td>
                  <td title={team["Games Played"] > 0
                    ? `Wygrane ${((team["Games Won"] / team["Games Played"]) * 100).toFixed(1)}% · Remisy ${((team["Games Drawn"] / team["Games Played"]) * 100).toFixed(1)}% · Przegrane ${((team["Games Lost"] / team["Games Played"]) * 100).toFixed(1)}%`
                    : 'Brak meczów'}>
                    {team["Games Played"] > 0
                      ? `${((team["Games Won"] / team["Games Played"]) * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                  <td
                    style={{
                      color: team["Rating Difference"] >= 0 ? "green" : "red",
                    }}
                  >
                    {team["Rating Difference"] >= 0 ? "+" : ""}
                    {team["Rating Difference"]}
                  </td>
                  <td>{team["Tournaments Played"]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-secondary"
              style={{ marginRight: "1rem" }}
            >
              Poprzednia
            </button>
            <span style={{ margin: "0 1rem" }}>
              Strona {currentPage} z {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-secondary"
              style={{ marginLeft: "1rem" }}
            >
              Następna
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamRankings;
